import { copyToClipboard } from '@ctw/shared/components/copy-to-clipboard';
import { Heading } from '@ctw/shared/components/heading';
import { notify } from '@ctw/shared/components/toast';
import { ReactHtmlRenderer } from '@ctw/shared/content/document-viewer/_internal/renderers/react/react-html-renderer';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useZuiContext } from '@ctw/shared/context/zui-provider';
import {
  DOCUMENT_VIEWER_SECTION_CLASSNAME,
  DOCUMENT_VIEWER_SECTION_HEADING_TEST_ID,
  DOCUMENT_VIEWER_SECTION_TEST_ID,
} from '@ctw/shared/test/ids';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { pathWithParameters } from '@ctw/shared/utils/url';
import { faLink } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { CdaSection } from '../../parsers/types';

interface SectionRendererProps {
  section: CdaSection;
}

export const ReactSectionRenderer = ({ section }: SectionRendererProps) => {
  const telemetry = useTelemetry();
  const { navigate } = useZuiContext();

  return section.id && section.title ? (
    <section
      id={section.domId}
      data-testid={DOCUMENT_VIEWER_SECTION_TEST_ID}
      className={twx('flex flex-col gap-4', DOCUMENT_VIEWER_SECTION_CLASSNAME)}
    >
      <Heading level="h2" className={tw`group m-0 block w-fit max-w-fit p-0`}>
        <a
          href={`#${section.domId}`}
          className={tw`flex h-full w-full items-center gap-2`}
          data-testid={DOCUMENT_VIEWER_SECTION_HEADING_TEST_ID}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();

            telemetry.trackInteraction('document_viewer_section_heading_click', {
              sectionTitle: section.title,
            });

            const sectionDeepLinkPath = pathWithParameters(
              `${window.location.pathname}${window.location.search}`,
              {
                documentSectionId: section.id,
              },
            );

            void copyToClipboard(
              `${window.location.protocol}//${window.location.host}${sectionDeepLinkPath}`,
            ).then(() => {
              navigate(sectionDeepLinkPath);
              notify({
                type: 'success',
                title: 'Copied Section Link',
                body: 'A shareable link to this document section has been copied to your clipboard.',
              });
            });
          }}
        >
          <FontAwesomeIcon
            className={tw`h-5 w-5 text-content-subtle opacity-0 transition-all duration-300 group-hover:opacity-50`}
            icon={faLink}
          />
          <span className={tw`font-medium text-xl leading-7`}>{section.title}</span>
        </a>
      </Heading>
      <div className={tw`flex w-full max-w-full flex-1 flex-col overflow-hidden pl-[28px]`}>
        {section.html && <ReactHtmlRenderer section={section} html={section.html} />}
      </div>
    </section>
  ) : (
    <></>
  );
};
